// /src/app/tools/seo-analyzer/page.tsx
'use client';

import { useState } from 'react';
import { Search, CheckCircle, AlertCircle, Info } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { seoAnalyzerContent } from '../../data/seo-analyzer-content';

interface SeoAnalysis {
    url: string;
    score: number;
    analysis: {
        title: {
            text: string;
            score: number;
            message: string;
        };
        description: {
            text: string;
            score: number;
            message: string;
        };
        headings: {
            h1: string;
            h2: string[];
            score: number;
            message: string;
        };
        images: {
            total: number;
            withAlt: number;
            score: number;
            message: string;
        };
    };
}

export default function SeoAnalyzer() {
    const [url, setUrl] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [analysis, setAnalysis] = useState<SeoAnalysis | null>(null);
    const [error, setError] = useState('');

    const analyzeSeo = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!url) return;

        setIsLoading(true);
        setError('');

        try {
            const response = await fetch(
                `/api/seo-analyzer?url=${encodeURIComponent(url)}`
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to analyze the webpage');
            }

            setAnalysis(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'An error occurred');
            setAnalysis(null);
        } finally {
            setIsLoading(false);
        }
    };

    const getScoreColor = (score: number) => {
        if (score >= 80) return 'text-green-500';
        if (score >= 50) return 'text-yellow-500';
        return 'text-red-500';
    };

    const renderScore = (score: number, label: string) => (
        <div className="flex items-center gap-4">
            <div className={`text-xl font-bold ${getScoreColor(score)}`}>
                {score}
            </div>

            <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                <div
                    className={`h-2.5 rounded-full ${score >= 80
                        ? 'bg-green-500'
                        : score >= 50
                            ? 'bg-yellow-500'
                            : 'bg-red-500'
                        }`}
                    style={{ width: `${score}%` }}
                />
            </div>

            <span className="text-sm text-gray-500 w-24">{label}</span>
        </div>
    );

    return (
        <div className="max-w-4xl mx-auto p-6">
            <ToolPageIntro title="SEO Analyzer">
                Analyze any URL for on-page SEO: title, meta description, headings, and content signals. Get a simple score and actionable tips. Enter a URL and run the check; no sign-up.
            </ToolPageIntro>

            {/* Form */}
            <form onSubmit={analyzeSeo} className="mb-8">
                <div className="flex gap-2">
                    <input
                        type="url"
                        value={url}
                        onChange={(e) => setUrl(e.target.value)}
                        placeholder="Enter URL (https://example.com)"
                        className="flex-1 p-3 border rounded-lg focus:ring-2 focus:ring-blue-500"
                        required
                    />

                    <button
                        type="submit"
                        disabled={isLoading}
                        className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
                    >
                        {isLoading ? 'Analyzing...' : 'Analyze'}
                        <Search size={18} />
                    </button>
                </div>
            </form>

            {/* Error */}
            {error && (
                <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6 flex items-center">
                    <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
                    <p className="text-red-700">{error}</p>
                </div>
            )}

            {/* Results */}
            {analysis && (
                <div className="space-y-8">
                    {/* Overall Score */}
                    <div className="bg-white rounded-lg shadow p-6">
                        <h2 className="text-2xl font-semibold mb-6 text-center">
                            SEO Score
                        </h2>

                        <div className="flex justify-center mb-8">
                            <div className="relative w-48 h-48">
                                <svg viewBox="0 0 100 100" className="w-full h-full">
                                    <circle
                                        strokeWidth="10"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="40"
                                        cx="50"
                                        cy="50"
                                        className="text-gray-200"
                                    />

                                    <circle
                                        strokeWidth="10"
                                        strokeLinecap="round"
                                        stroke="currentColor"
                                        fill="transparent"
                                        r="40"
                                        cx="50"
                                        cy="50"
                                        strokeDasharray={`${analysis.score * 2.51} 251`}
                                        className={getScoreColor(analysis.score)}
                                    />
                                </svg>

                                <div className="absolute inset-0 flex flex-col items-center justify-center">
                                    <span
                                        className={`text-4xl font-bold ${getScoreColor(
                                            analysis.score
                                        )}`}
                                    >
                                        {analysis.score}
                                    </span>
                                    <span className="text-sm text-gray-500">out of 100</span>
                                </div>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {renderScore(analysis.analysis.title.score, 'Title')}
                            {renderScore(analysis.analysis.description.score, 'Description')}
                            {renderScore(analysis.analysis.headings.score, 'Headings')}
                            {renderScore(analysis.analysis.images.score, 'Images')}
                        </div>
                    </div>

                    {/* Detailed Analysis */}
                    <div className="grid md:grid-cols-2 gap-6">
                        {/* Page Info */}
                        <div className="bg-white rounded-lg shadow p-6 space-y-6">
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <Info size={20} className="text-blue-500" />
                                Page Information
                            </h3>

                            {/* Title */}
                            <div>
                                <h4 className="font-medium text-gray-700">Title</h4>
                                <p className="text-gray-600">
                                    {analysis.analysis.title.text || 'No title found'}
                                </p>
                                <p className="text-sm mt-1 flex items-center text-gray-600">
                                    {analysis.analysis.title.score === 100 ? (
                                        <CheckCircle size={16} className="mr-1 text-green-600" />
                                    ) : (
                                        <AlertCircle size={16} className="mr-1 text-yellow-600" />
                                    )}
                                    {analysis.analysis.title.message}
                                </p>
                            </div>

                            {/* Meta Description */}
                            <div>
                                <h4 className="font-medium text-gray-700">
                                    Meta Description
                                </h4>
                                <p className="text-gray-600">
                                    {analysis.analysis.description.text ||
                                        'No meta description found'}
                                </p>
                                <p className="text-sm mt-1 flex items-center text-gray-600">
                                    {analysis.analysis.description.score === 100 ? (
                                        <CheckCircle size={16} className="mr-1 text-green-600" />
                                    ) : (
                                        <AlertCircle size={16} className="mr-1 text-yellow-600" />
                                    )}
                                    {analysis.analysis.description.message}
                                </p>
                            </div>
                        </div>

                        {/* Content Analysis */}
                        <div className="bg-white rounded-lg shadow p-6 space-y-6">
                            <h3 className="text-xl font-semibold flex items-center gap-2">
                                <Info size={20} className="text-blue-500" />
                                Content Analysis
                            </h3>

                            {/* Headings */}
                            <div>
                                <h4 className="font-medium text-gray-700">Headings</h4>

                                <div className="mt-2 bg-gray-50 p-3 rounded">
                                    <span className="font-mono text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">
                                        H1
                                    </span>
                                    <p className="mt-1 text-gray-600">
                                        {analysis.analysis.headings.h1 || 'No H1 found'}
                                    </p>
                                </div>

                                {analysis.analysis.headings.h2.length > 0 && (
                                    <ul className="mt-3 space-y-1">
                                        {analysis.analysis.headings.h2
                                            .slice(0, 5)
                                            .map((h2, i) => (
                                                <li key={i} className="text-sm text-gray-600">
                                                    • {h2}
                                                </li>
                                            ))}
                                    </ul>
                                )}

                                <p className="text-sm mt-2 flex items-center text-gray-600">
                                    {analysis.analysis.headings.score === 100 ? (
                                        <CheckCircle size={16} className="mr-1 text-green-600" />
                                    ) : (
                                        <AlertCircle size={16} className="mr-1 text-yellow-600" />
                                    )}
                                    {analysis.analysis.headings.message}
                                </p>
                            </div>

                            {/* Images */}
                            <div>
                                <h4 className="font-medium text-gray-700">Images</h4>

                                <p className="text-gray-600">
                                    {analysis.analysis.images.total === 0
                                        ? 'No images found'
                                        : `Found ${analysis.analysis.images.withAlt} of ${analysis.analysis.images.total} images with alt text`}
                                </p>

                                <p className="text-sm mt-1 flex items-center text-gray-600">
                                    {analysis.analysis.images.score === 100 ? (
                                        <CheckCircle size={16} className="mr-1 text-green-600" />
                                    ) : (
                                        <AlertCircle size={16} className="mr-1 text-yellow-600" />
                                    )}
                                    {analysis.analysis.images.message}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Informational Content */}
                    <div className="mt-12">
                        <InformationalContent
                            title={seoAnalyzerContent.title}
                            description={seoAnalyzerContent.description}
                            content={seoAnalyzerContent.content}
                            faqs={seoAnalyzerContent.faqs}
                            relatedTools={seoAnalyzerContent.relatedTools}
                        />
                    </div>
                </div>
            )}
        </div>
    );
}